import { AppDispatch, store } from "@/store/store";
import { fetchReservation } from "@/api/reservation.service";
import { fetchRestaurantById } from "@/api/restaurant.service";
import { setMenu } from "@/features/menu/menuSlice";
import { hydrateCartFromOrder } from "@/features/cart/orderToCart";
import {
  setReservationId,
  updateReservation,
} from "@/features/reservation/reservationSlice";

export async function refreshReservationAndMenu(
  dispatch: AppDispatch,
  reservationId: string,
): Promise<boolean> {
  // Step 1: fetch reservation to get restaurant id
  const reservationData: any = await fetchReservation(String(reservationId));
  const restaurantId: string | undefined =
    String(
      reservationData?._restaurantId ??
        reservationData?.restaurantId ??
        reservationData?.restaurant_id ??
        reservationData?.restaurant?.id ??
        "",
    ).trim() || undefined;

  if (!restaurantId) {
    return false;
  }

  const state = store.getState() as any;

  // Always fetch restaurant + menu for this reservation on refresh
  const restaurant: any = await fetchRestaurantById(
    restaurantId,
    String(reservationId),
  );

  if (restaurant?.name) {
    // Restaurant's IANA zone (e.g. "America/Los_Angeles"), used below to
    // format every guest-facing reservation/cutoff label. Computing from the
    // raw UTC instant + this zone (rather than trusting any pre-formatted
    // *_local field on the reservation document) means the label can't go
    // stale if the reservation is rescheduled and only the UTC field is
    // updated, and it covers order.lockAt too, which has no *_local sibling.
    // Falls back to the runtime default (the browser's own zone) when a
    // restaurant doc lacks it, matching prior behavior instead of throwing.
    const restaurantTimeZone: string | undefined =
      typeof restaurant?.timezone === "string" && restaurant.timezone
        ? restaurant.timezone
        : undefined;

    // Reservation dining time (for details card)
    const rawReservationTime: any = reservationData?.reservationTime;
    const reservationUtcIso: string | undefined =
      typeof rawReservationTime === "string"
        ? rawReservationTime
        : rawReservationTime?.$date
          ? String(rawReservationTime.$date)
          : undefined;
    const reservationDt = reservationUtcIso
      ? new Date(reservationUtcIso)
      : undefined;
    // The absolute instant, kept alongside the display strings below. Those
    // are locale-formatted (in the restaurant's zone) and cannot be re-parsed
    // reliably (see resolveReservationStart in lib/calendar.ts).
    const reservationAtIso =
      reservationDt && !Number.isNaN(reservationDt.getTime())
        ? reservationUtcIso
        : undefined;
    const reservationTimeLocal = reservationDt
      ? reservationDt.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          timeZone: restaurantTimeZone,
        })
      : "";
    const reservationDateLocal = reservationDt
      ? reservationDt.toLocaleDateString([], {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: restaurantTimeZone,
        })
      : "";

    // Pre-order cutoff time (lockAt) - prefer order.lockAt, then reservation-level, then fallback to reservationTime
    const rawLockAt: any =
      reservationData?.order?.lockAt ?? reservationData?.reservationTime;
    const lockUtcIso: string | undefined =
      typeof rawLockAt === "string"
        ? rawLockAt
        : rawLockAt?.$date
          ? String(rawLockAt.$date)
          : undefined;
    const lockDt = lockUtcIso ? new Date(lockUtcIso) : reservationDt;
    const lockAtMs = lockDt ? lockDt.getTime() : undefined;
    // Compact label in the restaurant's zone, computed from the UTC instant
    // (see restaurantTimeZone above) rather than any *_local field.
    const lockAtLabel = lockDt
      ? lockDt.toLocaleString([], {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZone: restaurantTimeZone,
        })
      : "";

    // Countdown state derived from lockAtMs
    const now = Date.now();
    const expiresAtMs = lockAtMs;
    const remainingMs = expiresAtMs ? Math.max(0, expiresAtMs - now) : 0;
    const remainingTotalSeconds = Math.floor(remainingMs / 1000);
    const remMinutes = Math.floor(remainingTotalSeconds / 60);
    const remSeconds = remainingTotalSeconds % 60;
    const timeRemainingLabel = `${String(remMinutes).padStart(2, "0")}:${String(remSeconds).padStart(2, "0")}`;
    const countdownActive = remainingMs <= 90 * 60 * 1000;

    // Update reservation fields; if we skipped fetching restaurant, reuse existing branding/name
    dispatch(
      updateReservation({
        reservationId: String(reservationId),
        restaurantId,
        restaurantName:
          restaurant?.name ?? state?.reservation?.restaurantName ?? "",
        logo: restaurant?.logo ?? state?.reservation?.logo ?? "",
        banner: restaurant?.banner ?? state?.reservation?.banner ?? "",
        address: restaurant?.address ?? state?.reservation?.address ?? "",
        reservationTime: reservationTimeLocal,
        reservationDate: reservationDateLocal,
        reservationAtIso,
        restaurantTimeZone,
        partySize: reservationData?.partySize ?? 1,
        timeRemainingLabel,
        expiresAtMs,
        lockAtMs,
        lockAtLabel,
        countdownActive,
        guestName: (reservationData?.guestName ?? "").trim(),
        phoneNumber: reservationData?.phoneNumber ?? "",
        loaded: true,
        order: reservationData?.order ?? undefined,
      }),
    );
    dispatch(setReservationId(String(reservationId)));
  } else {
    return false;
  }

  // Map menu + modifications for this restaurant
  if (restaurant) {
    const itemsSource: any[] = Array.isArray(restaurant?.menu)
      ? restaurant.menu
      : [];
    const modificationsSource: any[] = Array.isArray(restaurant?.modifications)
      ? restaurant.modifications
      : [];

    const categoriesMap = new Map<
      string,
      { id: string; name: string; itemIds: Set<string> }
    >();
    const itemsById: Record<string, any> = {};
    const modificationsById: Record<string, any> = {};

    for (const mod of modificationsSource) {
      // New schema: use _id or modificationId as the primary identifier
      const modId = mod?._id ?? mod?.modificationId ?? mod?.mod_id ?? mod?.id;
      if (!modId) continue;

      const group = {
        mod_id: String(modId),
        posId: mod?.pos?.id
          ? String(mod.pos.id)
          : mod?.posId
            ? String(mod.posId)
            : undefined,
        name: mod.name,
        displayName: mod.name ?? "",
        status: mod.status !== false, // Default to true if not specified
        data: Array.isArray(mod.data)
          ? mod.data.map((d: any) => ({
              id: String(d.id ?? d.posId ?? d.name ?? ""),
              displayName: String(d.displayName ?? d.name ?? ""),
              prices: d.prices ?? [],
              image: d.image,
              // capture POS option id for backend item.guid
              posId: d?.pos?.id
                ? String(d.pos.id)
                : d?.posId
                  ? String(d.posId)
                  : undefined,
            }))
          : [],
      } as any;
      modificationsById[String(modId)] = group;

      // Also index by modificationId if different from _id
      if (mod?.modificationId && String(mod.modificationId) !== String(modId)) {
        modificationsById[String(mod.modificationId)] = group;
      }

      // Also index by pos.id if available
      const posKey = mod?.pos?.id ?? mod?.posId;
      if (posKey && String(posKey) !== String(modId)) {
        modificationsById[String(posKey)] = group;
      }
    }

    const items = itemsSource.map((it: any) => {
      // POS item id. `pos` is nullable on a served item (an item with no POS
      // link), so this can be undefined - never stringify it unguarded.
      const posItemId: string | undefined = it?.pos?.id
        ? String(it.pos.id)
        : it?.posId
          ? String(it.posId)
          : undefined;

      // The served item's Mongo `_id` - the order line identity sent to the
      // backend as item.guid (FOYR-424 R15).
      const menuItemId: string | undefined =
        it?._id != null && String(it._id) !== "" ? String(it._id) : undefined;

      // Internal menu id (routes, category membership, cart lineIds) - keep
      // compatibility with existing ids but prefer POS id when available
      const idStr = String(
        posItemId ?? it.itemId ?? it._id ?? it.id ?? it.name ?? "",
      );

      // Handle prices - check if prices array exists and get first price
      const priceCents =
        Array.isArray(it.prices) && it.prices[0]?.price != null
          ? Number(it.prices[0].price)
          : 0;

      // New schema: categories is an array (e.g. ["Drinks", "Bites"]) AND
      // pos.categories is an array of POS category objects with posId + name.
      const rawCats: any[] = Array.isArray(it.categories) ? it.categories : [];
      const collectedCategoryIds: string[] = [];

      if (rawCats.length > 0 && typeof rawCats[0] === "object") {
        rawCats.forEach((catObj: any) => {
          const cid = String(
            catObj?.posId ?? catObj?.id ?? catObj?.name ?? "Uncategorized",
          );
          const cname = String(
            catObj?.name ??
              catObj?.displayName ??
              catObj?.id ??
              "Uncategorized",
          );
          if (!categoriesMap.has(cid)) {
            categoriesMap.set(cid, {
              id: cid,
              name: cname,
              itemIds: new Set(),
            });
          }
          categoriesMap.get(cid)!.itemIds.add(idStr);
          collectedCategoryIds.push(cid);
        });
      } else {
        const cats: string[] = rawCats.length > 0 ? rawCats : ["Uncategorized"];
        cats.forEach((catName) => {
          const cid = String(catName);
          const cname = String(catName);
          if (!categoriesMap.has(cid)) {
            categoriesMap.set(cid, {
              id: cid,
              name: cname,
              itemIds: new Set(),
            });
          }
          categoriesMap.get(cid)!.itemIds.add(idStr);
          collectedCategoryIds.push(cid);
        });
      }

      const posCategories: any[] = Array.isArray(it.pos?.categories)
        ? it.pos.categories
        : [];
      const posCategoryIds: string[] = posCategories
        .map((c: any) =>
          c?.posId ? String(c.posId) : c?.id ? String(c.id) : undefined,
        )
        .filter((cid: string | undefined): cid is string => !!cid);

      // New schema: modifiers use _modification instead of mod_id
      // Also check for availability and visible fields
      const isAvailable =
        it.visible !== false &&
        it.in_stock !== false &&
        it.availability?.alwaysAvailable !== false;

      const mapped = {
        id: idStr,
        name: String(it.name ?? "Item"),
        description: it.description ?? undefined,
        priceCents,
        imageUrl: it.image || undefined,
        isAvailable,
        caloriesLabel: undefined,
        categoryIds: collectedCategoryIds,
        posCategoryIds,
        posItemId,
        menuItemId,
        modifiers: Array.isArray(it.modifiers)
          ? it.modifiers.map((m: any) => ({
              // New schema: use _modification instead of mod_id
              mod_id: String(m._modification ?? m.mod_id ?? m.id ?? ""),
              required: m.required ?? false,
              minimum: m.minimum ?? 0,
              maximum:
                m.maximum ??
                (m.selectionType === "SINGLE" ? 1 : Number.POSITIVE_INFINITY),
              selectionType: m.selectionType ?? "MULTIPLE",
              status: m.status !== false, // Include status if available
            }))
          : [],
      };
      itemsById[idStr] = mapped;

      // Also index by _id if different from itemId - stored order lines
      // written since FOYR-424 carry the _id as item.guid.
      if (menuItemId && menuItemId !== idStr) {
        itemsById[menuItemId] = mapped;
      }

      // Also index by POS item id if different from internal id
      if (posItemId && posItemId !== idStr) {
        itemsById[posItemId] = mapped;
      }

      return mapped;
    });

    // Backend already provides the two desired categories ("Drinks" and "Bites"),
    // so we can use them directly without additional heuristics.
    const categories = Array.from(categoriesMap.values()).map((entry) => ({
      id: entry.id,
      name: entry.name,
      itemIds: Array.from(entry.itemIds),
    }));

    dispatch(
      setMenu({
        categories,
        items,
        itemsById,
        modificationsById: {}, // Modifications will be fetched on-demand when item pages open
        selectedCategoryId: categories[0]?.id ?? null,
      }),
    );

    // Populate the cart with whatever the guest has already ordered, using
    // the same item/modifier derivation "Edit Order" seeds with, so the
    // Menu/Cart screens never show "0 items" for a returning guest with a
    // placed order (the bug: /order-confirmed rendered the order fine, but
    // nothing hydrated the cart, so navigating to Menu — e.g. via "Add
    // more" — showed an empty cart).
    //
    // Guarded on there being existing order items: an empty/absent prior
    // order (first visit, or a same-reservation page refresh mid cart-build)
    // must NOT touch the cart, since ReservationLoader intentionally
    // preserves an in-progress, not-yet-submitted cart across a refresh of
    // the same reservationId. Modifications are looked up from the local
    // `modificationsById` built above rather than the store's (which is
    // deliberately left empty for on-demand loading) — this is a strict
    // superset of what "Edit Order" resolves at the same point in time,
    // never a regression.
    const orderItems: any[] = Array.isArray(reservationData?.order?.items)
      ? reservationData.order.items
      : [];
    if (orderItems.length > 0) {
      dispatch(
        hydrateCartFromOrder(
          orderItems,
          { items, itemsById, modificationsById },
          "replace",
        ),
      );
    }
  }
  return true;
}

const reservationBootstrap = { refreshReservationAndMenu };
export default reservationBootstrap;
